import { useState } from "react";
import { toast } from "@/state/hooks";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { Card, CardContent } from "@/ui/components/ui/card";
import { useActions } from "@/state/legacy_module_api";
import { ErrorLogger } from "@/utils/module_api";

const LockCodesPanel = () => {
  const { setCodes } = useActions().hilo;
  const [lowLevelCode, setLowLevelCode] = useState("");
  const [highLevelCode, setHighLevelCode] = useState("");
  const [destroyCode, setDestroyCode] = useState("");

  const handleCodeChange = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const filtered = value.replace(/\D/g, "").slice(0, 4);
    setter(filtered);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (highLevelCode === lowLevelCode) {
      toast.error("Lo-level code and hi-level code cannot be the same");
      return;
    }
    if (lowLevelCode.length !== 4 || highLevelCode.length !== 4) {
      toast.error("Codes must be 4 digits");
      return;
    }

    if (destroyCode) {
      if (highLevelCode === destroyCode || lowLevelCode === destroyCode) {
        toast.error(
          "Destroy code cannot be the same as hi-level code or lo-level code"
        );
        return;
      }
      if (destroyCode.length !== 4) {
        toast.error("Destroy code must be 4 digits");
        return;
      }
    }

    try {
      await setCodes({
        lo: lowLevelCode,
        hi: highLevelCode,
        destroy: destroyCode || undefined,
      });
      toast.success("Codes set successfully!");
    } catch (error) {
      ErrorLogger.logError(error as Error, "LockCodesPanel.tsx");
      toast.error("Failed to set codes");
    }
  };

  return (
    <div className="h-full overflow-auto p-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">Manage Lock Codes</h2>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="lo-level-code">Lo Level Code</Label>
                <Input
                  id="lo-level-code"
                  type="password"
                  inputMode="numeric"
                  placeholder="* * * *"
                  value={lowLevelCode}
                  onChange={(e) =>
                    handleCodeChange(e.target.value, setLowLevelCode)
                  }
                  maxLength={4}
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hi-level-code">Hi Level Code</Label>
                <Input
                  id="hi-level-code"
                  type="password"
                  inputMode="numeric"
                  placeholder="* * * *"
                  value={highLevelCode}
                  onChange={(e) =>
                    handleCodeChange(e.target.value, setHighLevelCode)
                  }
                  maxLength={4}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <Label htmlFor="destroy-level-code">Destroy Code</Label>
              <Input
                id="destroy-level-code"
                type="password"
                inputMode="numeric"
                placeholder="* * * *"
                value={destroyCode}
                onChange={(e) =>
                  handleCodeChange(e.target.value, setDestroyCode)
                }
                maxLength={4}
                autoComplete="new-password"
              />
            </div>
          </CardContent>
        </Card>

        <Button type="submit" className="w-full">
          I have set and memorized my code
        </Button>

        <Card className="border-destructive/50">
          <CardContent className="pt-6">
            <p className="font-semibold text-destructive text-center">
              Failing to remember your four-digit codes can result in permanent
              lockout from the application.
            </p>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default LockCodesPanel;
